import { createActorContext } from "@xstate/react";
import isEmpty from "lodash-es/isEmpty";
import mergeWith from "lodash-es/mergeWith";
import {
	type ActorRefFrom,
	type EventObject,
	assign,
	createActor,
	emit,
	enqueueActions,
	fromCallback,
	fromPromise,
	raise,
	setup,
	stopChild,
	waitFor,
} from "xstate";

import type { DocumentMetadata } from "@moment/api-collab/api-types";
import { docsApi } from "@moment/app/state/api/docsapi";
import { isRunningInIframe } from "@moment/ui";

import { isDesktopAppMode } from "~/auth/interop/common";
import {
	exists,
	getDocumentDirPath,
	getDocumentsDirPath,
	getMomentGitConfigPath,
	readDir,
} from "~/data/filesystem";
import { type DocumentListItem, type Workspace, WorkspaceSchema } from "~/state/types";
import { LOCAL_WORKSPACE_NAME } from "~/strings";

import { filesystemApi } from "../../api/filesystem";
import { sqliteApi } from "../../api/sqlite";
import { atlasMachine } from "../atlas/atlas.machine";
import {
	type AuthenticationMachine,
	authenticationActorRef,
	getAccessToken,
} from "../authentication";
import { createDocumentMachine } from "../documents/create-document.machine";
import { documentMachine } from "../documents/document.machine";
import { featureFlagsMachine } from "../feature-flags/feature-flags.machine";
import { gitConfigMachine } from "../git/config/git-config.machine";
import { createQueryMachine } from "../query.machine";

// const debuglog = (_msg, ...args) => {
// 	console.info(`[app machine] ${_msg}`, ...args);
// };
const debuglog = (_msg: string, ..._args: unknown[]) => {};

const fetchWorkspacesQuery = createQueryMachine<Workspace[]>({
	queryKey: "workspaces",
	queryFn: docsApi.workspaces.list,
	loadPersistedData: () => sqliteApi.workspaces.listAll(),
	persistData: async (data) => {
		await Promise.all(data.map(sqliteApi.workspaces.upsert));
	},
	shouldEnd: true,
});

const fetchRemoteDocumentListQuery = fromPromise<DocumentListItem[], { workspaces: Workspace[] }>(
	async ({ input }) => {
		const { workspaces } = input;

		const machines = workspaces.map((workspace) =>
			createQueryMachine<DocumentListItem[]>({
				queryKey: `documents-${workspace.id}`,
				queryFn: async () => {
					debuglog("Fetching remote documents for workspace", {
						workspaceName: workspace.name,
					});
					const accessToken = await getAccessToken();

					const repos = await docsApi.workspaces.queryDocuments(
						accessToken!,
						workspace.name
					);

					debuglog("repos found", {
						workspace,
						repos,
					});

					return repos.map(
						(repo) =>
							({
								id: repo.name,
								title: repo.moment_title,
								createdAt: new Date(repo.created_at),
								updatedAt: new Date(repo.updated_at),
								template: repo.template,
								workspaces: [
									{
										id: workspace.id,
										name: workspace.name,
										host: workspace.host,
										slug: workspace.slug,
										owner: workspace.owner,
										role: workspace.role,
									},
								],
							}) as DocumentListItem
					);
				},
				shouldEnd: true,
			})
		);

		const actors = machines.map((machine) => createActor(machine, {}));

		const documents = await Promise.allSettled(
			actors.map(async (actor) => {
				actor.start();

				const result = await waitFor(actor, (state) => state.status === "done");

				return result.output ?? [];
			})
		);

		return documents.filter((x) => x.status === "fulfilled").flatMap((x) => x.value) ?? [];
	}
);

const fetchDocumentsFromCache = fromPromise<DocumentListItem[], void>(async () => {
	return isDesktopAppMode() ? sqliteApi.documents.listAll() : [];
});

const fetchDocumentsFromDisk = fromPromise<DocumentListItem[], void>(async () => {
	if (!isDesktopAppMode()) {
		return [];
	}

	const dirPath = await getDocumentsDirPath();
	if (!(await exists(dirPath))) {
		return [];
	}

	const dirList = await readDir(dirPath).then((list) =>
		list
			// We only care about directories that are not symlinks
			.filter((file) => file.isDirectory && !file.isSymlink)
			// We don't want to include hidden files
			.filter((file) => !file.name.startsWith("."))
	);

	const documentMetadatas: Record<string, DocumentMetadata> = {};

	await Promise.all(
		dirList.map(async (dir) => {
			try {
				const documentDirPath = await getDocumentDirPath(dir.name);

				const metadata = await filesystemApi.document.metadata.read(documentDirPath);

				if (!metadata) {
					return;
				}
				documentMetadatas[documentDirPath] = metadata;

				return metadata;
			} catch (error) {
				console.error("unable to read or parse document metadata", {
					dir: dir.name,
					error,
				});
			}
		})
	);

	return (
		Object.entries(documentMetadatas).map(
			([path, metadata]) =>
				({
					id: metadata.id,
					path,
					title: metadata.title,
					createdAt: metadata.created,
					updatedAt: metadata.created,
					workspaces: [
						{
							id: LOCAL_WORKSPACE_NAME,
							name: LOCAL_WORKSPACE_NAME,
							host: "",
							slug: LOCAL_WORKSPACE_NAME,
							// this is local workspace,
							// so the owner would be the client.
							owner: true,
						},
					],
					template: false,
				}) as DocumentListItem
		) ?? []
	);
});

const writeDocumentsToCache = fromPromise<void, { documents: DocumentListItem[] }>(
	async ({ input }) => {
		const { documents } = input;

		await Promise.all(documents.map(sqliteApi.documents.upsert));
	}
);

export type AppMachineContext = {
	workspaces: Record<string, Workspace>;
	documents: Record<string, DocumentListItem>;
	documentActorRefs: Record<string, ActorRefFrom<typeof documentMachine>>;
	authenticationActorRef: ActorRefFrom<AuthenticationMachine>;
	atlasActorRef: ActorRefFrom<typeof atlasMachine>;
	gitConfigActorRef?: ActorRefFrom<typeof gitConfigMachine>;
	featureFlagActorRef?: ActorRefFrom<typeof featureFlagsMachine>;
};

export type AppMachineInput = {
	documents: Record<string, DocumentListItem>;
};

export type AppMachineEvents =
	| { type: "workspaceList.refetch" }
	| { type: "documentList.refetch" }
	| { type: "documentList.refetchRemote" }
	| {
			type: "documentList.createDocument";
			payload?:
				| {
						title?: string;
						body?: string;
						bodyType?: "html" | "text" | undefined;
				  }
				| { templateWorkspaceId: string; templateId: string };
	  }
	| {
			type: "documentList.updated";
			payload: { listItems: DocumentListItem[] };
			from: "remote" | "cache" | "disk";
	  }
	| {
			type: "documentList.updated.workspace";
			payload: { workspace: Workspace; documentId: string };
	  }
	| { type: "document.deleted"; payload: { documentId: string } };

export type AppMachineEmitted = {
	type: "document.created";
	payload: DocumentListItem;
};

export const appMachine = setup({
	types: {
		input: {} as AppMachineInput,
		context: {} as AppMachineContext,
		events: {} as AppMachineEvents,
		emitted: {} as AppMachineEmitted,
	},
	actors: {
		listenForLogin: fromCallback<
			EventObject,
			{ authenticationActorRef: ActorRefFrom<AuthenticationMachine> }
		>(({ sendBack, input }) => {
			input.authenticationActorRef.on("authn.succeeded", (event) => {
				debuglog("Detected login, refetching workspace list", { event });
				sendBack({ type: "workspaceList.refetch" });
				sendBack({ type: "documentList.refetch" });
			});
		}),
		fetchWorkspacesQuery,
		fetchDocumentsFromCache,
		fetchDocumentsFromDisk,
		writeDocumentsToCache,
		fetchRemoteDocumentListQuery,
		createNewDocument: createDocumentMachine,
		getMomentDir: fromPromise<string>(getMomentGitConfigPath),
	},
	actions: {
		updateDocumentListItems: assign(({ context, event, spawn }) => {
			if (event.type !== "documentList.updated") {
				// Update nothing
				return {};
			}

			debuglog("Updating document list items", {
				event,
				olddocuments: context.documents,
			});

			const documentsCopy = structuredClone(context.documents);
			const newDocumentActorRefs = { ...context.documentActorRefs };

			// listItems is the updated list that came from the event.
			event.payload.listItems.forEach((updatedListItem) => {
				// Merge the workspaces from the list item with the existing document
				if (documentsCopy[updatedListItem.id]) {
					// save the old title, in case the title comes from the filesystem.
					const oldListItemTitle = documentsCopy[updatedListItem.id].title;

					// okay I finally figured out how this function works.
					// this merges the values of an object on an attribute level.
					const newDocumentListItem = mergeWith(
						documentsCopy[updatedListItem.id],
						updatedListItem,
						(oldValue, newValue, key) => {
							// Don't overwrite the existing value with undefined
							if (
								typeof oldValue !== "undefined" &&
								typeof newValue === "undefined"
							) {
								return oldValue;
							}

							if (key === "title") {
								// if we get an remote update, and the cache has a title,
								// that means the user has a local copy, which we should return.
								if (event.from === "remote") {
									return isEmpty(oldValue) ? newValue : oldValue;
								}
								return newValue;
							}

							if (
								key === "workspaces" &&
								Array.isArray(oldValue) &&
								Array.isArray(newValue)
							) {
								// dedup workspaces from event.
								const dedupMap = new Map<string, Workspace>();
								oldValue.forEach((workspace: Workspace) => {
									dedupMap.set(workspace.id, workspace);
								});
								newValue.forEach((workspace: Workspace) => {
									dedupMap.set(workspace.id, workspace);
								});

								// I am sure there is a bette way to do this.
								// but we want the local workspace to be at the end of the list.
								// as the sync machine expects the remote workspace to be first.
								// we also do not have a way to choose which workspace you want to publish to right now
								// and assume you want to publish to the workspace[0] everywhere.
								// long-term solution is allowing you to choose from a filtered list,
								// which I can create in a different PR.
								// But want to stabilize the app first.
								const mergedWorkspaces = Array.from(dedupMap.values());
								mergedWorkspaces.sort((a, b) => {
									return a.id === LOCAL_WORKSPACE_NAME ? 1 : -1;
								});
								return mergedWorkspaces;
							}

							// if this is an array, this is a list of workspaces.
							// lets validate that and then process them accordingly.
							if (Array.isArray(newValue) && Array.isArray(oldValue)) {
								return newValue.concat(oldValue);
							}
						}
					);

					// Update the document actor with all the new data
					newDocumentActorRefs[updatedListItem.id]?.send({
						type: "document.listItemChanged",
						payload: {
							listItem: newDocumentListItem,
						},
					});
				} else {
					documentsCopy[updatedListItem.id] = updatedListItem;
					newDocumentActorRefs[updatedListItem.id] = spawn(documentMachine, {
						systemId: `document:${updatedListItem.id}`,
						input: {
							listItem: updatedListItem,
							authenticationActorRef,
						},
					});
				}
			});

			return {
				documents: documentsCopy,
				documentActorRefs: newDocumentActorRefs,
			};
		}),
	},
	guards: {
		isDesktopAppMode,
		isUserAuthenticated: ({ context }) => {
			return (
				context.authenticationActorRef.getSnapshot().context.authnStatus === "authenticated"
			);
		},
	},
}).createMachine({
	id: "app",
	type: "parallel",
	entry: [
		({ event, context }) => {
			debuglog("App machine started", {
				isRunningInIframe: isRunningInIframe(),
				event,
				context,
			});
		},
	],
	context: ({ input, spawn }) => {
		return {
			workspaces: {},
			documents: input?.documents ?? {},
			documentActorRefs: Object.fromEntries(
				Object.entries(input?.documents ?? {}).map(([id, document]) => [
					id,
					spawn(documentMachine, {
						systemId: `document:${id}`,
						input: { listItem: document, authenticationActorRef },
					}),
				])
			),
			authenticationActorRef,
			atlasActorRef: spawn(atlasMachine),
			featureFlagActorRef: spawn(featureFlagsMachine),
		};
	},
	states: {
		loginListener: {
			invoke: {
				src: "listenForLogin",
				input: ({ context }) => ({
					authenticationActorRef: context.authenticationActorRef,
				}),
			},
		},
		gitConfig: {
			description: "Manage the git configuration",
			initial: "initializing",
			states: {
				initializing: {
					always: [{ guard: "isDesktopAppMode", target: "loading" }, { target: "error" }],
				},
				loading: {
					invoke: {
						src: "getMomentDir",
						onDone: {
							actions: assign(({ event, spawn }) => {
								const path = event.output;

								return {
									gitConfigActorRef: spawn(gitConfigMachine, {
										input: { path },
									}),
								};
							}),
							target: "ready",
						},
						onError: {
							target: "error",
						},
					},
				},
				ready: {},
				error: {},
			},
		},

		workspaceList: {
			description: "Manage the workspaces list",
			type: "parallel",
			guard: "isUserAuthenticated",
			states: {
				fetchWorkspaces: {
					initial: "fetching",
					states: {
						fetching: {
							invoke: {
								src: "fetchWorkspacesQuery",
								onDone: {
									target: ["done"],
									actions: [
										({ context }) =>
											debuglog("Fetched workspaces", {
												workspaces: context.workspaces,
											}),
										assign(({ context, event }) => ({
											workspaces: [
												...Object.values(context.workspaces),
												...(event.output ?? []),
											].reduce(
												(acc, workspace) => {
													acc[workspace.id] = workspace;
													return acc;
												},
												{} as Record<string, Workspace>
											),
										})),
										raise({
											type: "documentList.refetchRemote",
										}),
									],
								},
							},
						},
						done: {},
					},
				},
			},
		},

		documentList: {
			description: "Manage the list of documents and spawn individual document actors",
			type: "parallel",
			states: {
				local: {
					initial: "fetching",
					states: {
						fetching: {
							invoke: {
								src: "fetchDocumentsFromCache",
								onDone: {
									target: "done",
									actions: raise(({ event }) => ({
										type: "documentList.updated",
										payload: { listItems: event.output ?? [] },
										from: "cache",
									})),
								},
							},
						},
						creating: {
							invoke: {
								src: "createNewDocument",
								input: ({ event }) =>
									(event.type === "documentList.createDocument" &&
										event.payload) ||
									{},
								onError: {
									actions: ({ event }) => {
										console.error("error creating document", { event });
									},
									target: "done",
								},
								onDone: {
									actions: [
										raise(({ event }) => ({
											type: "documentList.updated",
											payload: { listItems: [event.output] },
											// when creating a document,
											// we create a new file, so say we are updating from disk
											from: "disk",
										})),
										emit(({ event }) => ({
											type: "document.created",
											payload: event.output,
										})),
									],
									target: "done",
								},
							},
						},
						done: {
							type: "final",
						},
					},
				},
				remote: {
					initial: "loading",
					states: {
						loading: {
							always: [
								{
									guard: "isUserAuthenticated",
									target: "fetching",
								},
								{
									target: "done",
								},
							],
						},
						fetching: {
							invoke: {
								src: "fetchRemoteDocumentListQuery",
								input: ({ context }) => ({
									workspaces: Object.values(context.workspaces),
								}),
								onDone: {
									actions: [
										({ context, event }) =>
											debuglog("Fetched remote document list", {
												event,
												context,
											}),
										raise(({ event }) => ({
											type: "documentList.updated",
											payload: { listItems: event.output ?? [] },
											from: "remote",
										})),
									],
									target: "#app.documentList.remote.done",
								},
								onError: {
									actions: ({ event }) => {
										console.error("error fetching remote document list", {
											event,
										});
									},
									target: "#app.documentList.remote.error",
								},
							},
						},
						done: {
							type: "final",
						},
						error: {
							type: "final",
						},
					},
				},
			},
			on: {
				"documentList.refetch": {
					target: [
						"#app.documentList.local.fetching",
						"#app.documentList.remote.fetching",
					],
				},
				"documentList.refetchRemote": {
					guard: "isUserAuthenticated",
					target: "#app.documentList.remote.fetching",
				},
				"documentList.createDocument": {
					target: "#app.documentList.local.creating",
				},
				"document.deleted": {
					actions: [
						stopChild(
							({ context, event }) =>
								context.documentActorRefs[event.payload.documentId]!
						),
						assign(({ context, event }) => ({
							documents: Object.fromEntries(
								Object.entries(context.documents).filter(
									([id]) => id !== event.payload.documentId
								)
							),
							documentActorRefs: Object.fromEntries(
								Object.entries(context.documentActorRefs).filter(
									([id]) => id !== event.payload.documentId
								)
							),
						})),
					],
					target: [
						"#app.documentList.local.fetching",
						"#app.documentList.remote.fetching",
					],
				},
			},
		},

		disk: {
			description: "Sync the list of documents from disk to the cache",
			initial: "readingDisk",
			guard: "isDesktopAppMode",
			states: {
				readingDisk: {
					invoke: {
						src: "fetchDocumentsFromDisk",
						onDone: {
							actions: raise(({ event }) => ({
								type: "documentList.updated",
								payload: { listItems: event.output ?? [] },
								from: "disk",
							})),
							target: "writingCache",
						},
					},
				},
				writingCache: {
					invoke: {
						src: "writeDocumentsToCache",
						input: ({ context }) => ({
							// Only write documents that exist on disk to the cache
							documents: Object.values(context.documents).filter(
								(document) => document.path !== undefined
							),
						}),
						onDone: {
							target: "done",
						},
						onError: {
							target: "done",
						},
					},
				},
				done: {
					on: {
						"documentList.refetch": {
							target: "readingDisk",
						},
					},
				},
			},
		},
	},

	on: {
		"documentList.updated": {
			actions: {
				type: "updateDocumentListItems",
				params: ({ event }) => ({ listItems: event.payload.listItems }),
			},
		},
		"workspaceList.refetch": {
			target: "#app.workspaceList.fetchWorkspaces.fetching",
		},
	},
});

export const AppActorContext: ReturnType<typeof createActorContext<typeof appMachine>> =
	createActorContext(appMachine);
export const useAppActor = AppActorContext.useActorRef;
export const useAppActorSelector = AppActorContext.useSelector;
